import { useState, type FormEvent } from "react";
import { fieldValue } from "./form.js";
import {
  requestPasswordReset,
  signIn,
  type NewPasswordChallenge,
  type ResetChallenge,
  type SecondStepChallenge,
  type Session,
} from "./auth.js";
import { SecondStepForm } from "./SecondStep.js";
import {
  describeAuthError,
  describeCodeDestination,
  humaniseAttribute,
  normaliseCode,
  passwordProblem,
  resetProblem,
  PASSWORD_HINT,
} from "./password.js";

export function SignIn({ onSignedIn }: { onSignedIn: (s: Session) => void }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set when Cognito answers SRP with NEW_PASSWORD_REQUIRED. The challenge
  // lives on the CognitoUser that raised it, so the step stays on this screen
  // — there is no session yet to route anywhere with.
  const [challenge, setChallenge] = useState<NewPasswordChallenge | null>(null);
  // Set when the password was right and a second step is due (#92).
  const [secondStep, setSecondStep] = useState<SecondStepChallenge | null>(null);
  // Forgotten-password reset. A separate screen rather than another field:
  // the code arrives by email, which means leaving the tablet and coming
  // back, and a half-filled sign-in form is the wrong thing to come back to.
  const [resetting, setResetting] = useState(false);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    // What the boxes hold, which is not always what React was told: a
    // password manager or a restored page fills them without an onChange,
    // and the empty state went to Cognito as an empty username (form.ts).
    const typed = fieldValue(e.currentTarget, "email", email).trim();
    const secret = fieldValue(e.currentTarget, "password", password);
    setEmail(typed);
    setPassword(secret);
    setBusy(true);
    setError(null);
    try {
      const result = await signIn(typed, secret);
      if (result.kind === "session") onSignedIn(result.session);
      else if (result.kind === "second-step") {
        setSecondStep(result.challenge);
        setPassword("");
      } else {
        setChallenge(result.challenge);
        setPassword("");
      }
    } catch (err) {
      setError(describeAuthError(err, "sign-in"));
    } finally {
      setBusy(false);
    }
  }

  if (resetting) {
    return (
      <ForgotPassword
        email={email.trim()}
        onSignedIn={onSignedIn}
        onCancel={() => {
          setResetting(false);
          setPassword("");
          setError(null);
        }}
      />
    );
  }

  if (secondStep) {
    return (
      <div className="signin">
        <SecondStepForm
          heading={
            <div>
              <div className="brand">Bar OS</div>
              <div className="label" style={{ marginTop: 6 }}>Two-step sign-in</div>
            </div>
          }
          challenge={secondStep}
          onSignedIn={onSignedIn}
          onCancel={() => {
            setSecondStep(null);
            setError(null);
          }}
        />
      </div>
    );
  }

  if (challenge) {
    return (
      <SetPassword
        challenge={challenge}
        onSignedIn={onSignedIn}
        onCancel={() => {
          setChallenge(null);
          setPassword("");
          setError(null);
        }}
      />
    );
  }

  return (
    <div className="signin">
      <form onSubmit={submit}>
        <div>
          {/* Generic on purpose: the org and venue appear in the sidebar once
              the person is signed in, and the page serves every customer. */}
          <div className="brand">Bar OS</div>
          <div className="label" style={{ marginTop: 6 }}>Sign in</div>
        </div>
        <label className="field">
          <span>Email</span>
          <input
            type="email"
            name="email"
            autoComplete="username"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
        </label>
        <label className="field">
          <span>Password</span>
          <input
            type="password"
            name="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        {error && <div className="msg err">{error}</div>}
        <button className="btn primary" type="submit" disabled={busy}>
          {busy ? "Signing in…" : "Sign in"}
        </button>
        <button
          className="btn"
          type="button"
          onClick={() => {
            setResetting(true);
            setError(null);
          }}
          disabled={busy}
        >
          Forgotten your password?
        </button>
      </form>
    </div>
  );
}

/**
 * The first sign-in of an invited account: replace the temporary password
 * Cognito emailed with one of their own.
 *
 * Typed twice, and revealable. A typo here is not a nuisance — the temporary
 * password is spent by the attempt, so a password nobody knows locks the
 * person out until an admin invites them again, and the first person through
 * this screen is usually the only admin their org has.
 */
function SetPassword({
  challenge,
  onSignedIn,
  onCancel,
}: {
  challenge: NewPasswordChallenge;
  onSignedIn: (s: Session) => void;
  onCancel: () => void;
}) {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [reveal, setReveal] = useState(false);
  const [extra, setExtra] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const problem = passwordProblem(password, confirm);
    if (problem) return setError(problem);
    setBusy(true);
    setError(null);
    try {
      onSignedIn(await challenge.complete(password, extra));
    } catch (err) {
      setError(describeAuthError(err, "new-password"));
      setBusy(false);
    }
  }

  return (
    <div className="signin">
      <form onSubmit={submit}>
        <div>
          <div className="brand">Bar OS</div>
          <div className="label" style={{ marginTop: 6 }}>Set a password</div>
        </div>
        <div className="msg">
          First sign-in for <strong>{challenge.email}</strong>. Choose a password to
          replace the temporary one. {PASSWORD_HINT}
        </div>
        <label className="field">
          <span>New password</span>
          <input
            type={reveal ? "text" : "password"}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoFocus
            required
          />
        </label>
        {/* Confirmation is redundant once revealed, but asking for it anyway
            keeps the form one shape and costs a revealing user nothing. */}
        <label className="field">
          <span>Repeat password</span>
          <input
            type={reveal ? "text" : "password"}
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
        </label>
        {challenge.missingAttributes.map((name) => (
          <label className="field" key={name}>
            <span>{humaniseAttribute(name)}</span>
            <input
              type="text"
              value={extra[name] ?? ""}
              onChange={(e) => setExtra((prev) => ({ ...prev, [name]: e.target.value }))}
              required
            />
          </label>
        ))}
        <button
          className="btn sm"
          type="button"
          aria-pressed={reveal}
          onClick={() => setReveal((r) => !r)}
        >
          {reveal ? "Hide password" : "Show password"}
        </button>
        {error && <div className="msg err">{error}</div>}
        <button className="btn primary" type="submit" disabled={busy}>
          {busy ? "Setting password…" : "Set password and sign in"}
        </button>
        <button className="btn" type="button" onClick={onCancel} disabled={busy}>
          Start again
        </button>
      </form>
    </div>
  );
}

/**
 * A forgotten password, reset without an admin.
 *
 * Two steps on one screen: ask for a code, then spend it on a new password.
 * The step the person is on is `challenge !== null` rather than its own enum —
 * the challenge is what the second step needs, so there is no state to get out
 * of step with.
 *
 * The new password is typed twice and revealable, for the same reason as the
 * first-sign-in screen: the code is spent by the attempt, so a typo means
 * asking for another one.
 */
function ForgotPassword({
  email: signInEmail,
  onSignedIn,
  onCancel,
}: {
  email: string;
  onSignedIn: (s: Session) => void;
  onCancel: () => void;
}) {
  // Carried over from the sign-in form, where they have usually typed it
  // already, and editable in case the reason they cannot get in is a typo.
  const [email, setEmail] = useState(signInEmail);
  const [challenge, setChallenge] = useState<ResetChallenge | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [reveal, setReveal] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function sendTo(next: ResetChallenge) {
    setChallenge(next);
    setSent(describeCodeDestination(next.destination));
    setCode("");
    setError(null);
  }

  async function request(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const typed = fieldValue(e.currentTarget, "email", email).trim();
    setEmail(typed);
    setBusy(true);
    setError(null);
    try {
      sendTo(await requestPasswordReset(typed));
    } catch (err) {
      setError(describeAuthError(err, "reset-request"));
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    if (!challenge) return;
    setBusy(true);
    setError(null);
    try {
      sendTo(await challenge.resend());
    } catch (err) {
      setError(describeAuthError(err, "reset-request"));
    } finally {
      setBusy(false);
    }
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!challenge) return;
    const problem = resetProblem(code, password, confirm);
    if (problem) return setError(problem);
    setBusy(true);
    setError(null);
    try {
      // Resolves to a session: the reset signs in with the new password
      // rather than handing the person back to the form they came from.
      onSignedIn(await challenge.confirm(normaliseCode(code), password));
    } catch (err) {
      setError(describeAuthError(err, "reset-confirm"));
      setBusy(false);
    }
  }

  if (!challenge) {
    return (
      <div className="signin">
        <form onSubmit={request}>
          <div>
            <div className="brand">Bar OS</div>
            <div className="label" style={{ marginTop: 6 }}>Reset your password</div>
          </div>
          <div className="msg">
            Enter the address you sign in with and we will email you a code to set a
            new password with.
          </div>
          <label className="field">
            <span>Email</span>
            <input
              type="email"
              name="email"
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoFocus
              required
            />
          </label>
          {error && <div className="msg err">{error}</div>}
          <button className="btn primary" type="submit" disabled={busy}>
            {busy ? "Sending…" : "Email me a code"}
          </button>
          <button className="btn" type="button" onClick={onCancel} disabled={busy}>
            Back to sign in
          </button>
        </form>
      </div>
    );
  }

  return (
    <div className="signin">
      <form onSubmit={submit}>
        <div>
          <div className="brand">Bar OS</div>
          <div className="label" style={{ marginTop: 6 }}>Set a new password</div>
        </div>
        <div className="msg">
          {sent} Enter it below with the password you want for{" "}
          <strong>{challenge.email}</strong>. {PASSWORD_HINT}
        </div>
        <label className="field">
          <span>Code from the email</span>
          <input
            type="text"
            // The OS offers the emailed code straight from the notification
            // with this, and a tablet shows a keypad rather than a keyboard.
            autoComplete="one-time-code"
            inputMode="numeric"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoFocus
            required
          />
        </label>
        <label className="field">
          <span>New password</span>
          <input
            type={reveal ? "text" : "password"}
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </label>
        <label className="field">
          <span>Repeat password</span>
          <input
            type={reveal ? "text" : "password"}
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            required
          />
        </label>
        <button
          className="btn sm"
          type="button"
          aria-pressed={reveal}
          onClick={() => setReveal((r) => !r)}
        >
          {reveal ? "Hide password" : "Show password"}
        </button>
        {error && <div className="msg err">{error}</div>}
        <button className="btn primary" type="submit" disabled={busy}>
          {busy ? "Setting password…" : "Set password and sign in"}
        </button>
        <button className="btn" type="button" onClick={resend} disabled={busy}>
          Send a new code
        </button>
        <button className="btn" type="button" onClick={onCancel} disabled={busy}>
          Back to sign in
        </button>
      </form>
    </div>
  );
}
